import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Navigate } from "react-router-dom";
import GlassPanel from "@/components/GlassPanel";
import PlayerCard from "@/components/PlayerCard";
import PaginationBar from "@/components/PaginationBar";
import EventScoringModal from "@/components/EventScoringModal";
import AdminScoreDialog from "@/components/AdminScoreDialog";
import VolunteerRoleDialog from "@/components/VolunteerRoleDialog";
import { toPng } from "html-to-image";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { SPORTS as SPORTS_FALLBACK, ATHLETE_CLASSES } from "@/lib/sports";

const JOINED_FMT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return "—"; }
};

const USERS_COLS = [
  { key: "public_id",          label: "ID",          get: (u) => (u.public_id || "") },
  { key: "name",               label: "Name",        get: (u) => (u.name || "").toLowerCase() },
  { key: "email",              label: "Email",       get: (u) => (u.email || "").toLowerCase() },
  { key: "phone",              label: "Phone",       get: (u) => (u.phone || "") },
  { key: "gender",             label: "Gender",      get: (u) => (u.gender || "") },
  { key: "dob",                label: "DOB",         get: (u) => (u.dob || "") },
  { key: "age",                label: "Age",         get: (u) => (u.age ?? -1) },
  { key: "athlete_class",      label: "Class",       get: (u) => (u.athlete_class || "regular") },
  { key: "primary_sport",      label: "Sport",       get: (u) => (u.primary_sport || "").toLowerCase() },
  { key: "country",            label: "Country",     get: (u) => (u.country || "").toLowerCase() },
  { key: "city",               label: "City",        get: (u) => (u.city || "").toLowerCase() },
  { key: "gym",                label: "Institution", get: (u) => (u.gym || "").toLowerCase() },
  { key: "created_at",         label: "Joined",      get: (u) => (u.created_at || "") },
  { key: "current_gps",        label: "GPS / PRO",   get: (u) => (u.current_gps ?? -1) },
  { key: "role",               label: "Role",        get: (u) => `${u.role || "user"}${u.is_volunteer ? "+vol" : ""}` },
  { key: null,                 label: "Actions",     get: () => 0 },
];

// Column configs for every other sortable admin table.
const ACTIVE_COLS = [
  { key: "last_login",   label: "Last Login",  get: (u) => (u.last_login || "") },
  { key: "name",         label: "Name",        get: (u) => (u.name || "").toLowerCase() },
  { key: "email",        label: "Email",       get: (u) => (u.email || "").toLowerCase() },
  { key: "phone",        label: "Phone",       get: (u) => (u.phone || "") },
  { key: "country",      label: "Country",     get: (u) => (u.country || "").toLowerCase() },
  { key: "gym",          label: "Institution", get: (u) => (u.gym || "").toLowerCase() },
  { key: "current_gps",  label: "GPS",         get: (u) => (u.current_gps ?? -1) },
  { key: "login_count",  label: "Logins",      get: (u) => (u.login_count || 0) },
];

const SCORES_COLS = [
  { key: "created_at",     label: "Submitted",   get: (s) => (s.created_at || "") },
  { key: "name",           label: "Athlete",     get: (s) => (s.snapshot?.name || s.owner?.name || "").toLowerCase() },
  { key: "email",          label: "Email",       get: (s) => (s.owner?.email || "").toLowerCase() },
  { key: "gender",         label: "Gender",      get: (s) => (s.snapshot?.gender || s.owner?.gender || "") },
  { key: "age",            label: "Age",         get: (s) => (s.snapshot?.age ?? -1) },
  { key: "athlete_class",  label: "Class",       get: (s) => (s.snapshot?.athlete_class || "") },
  { key: "primary_sport",  label: "Sport",       get: (s) => (s.snapshot?.primary_sport || "").toLowerCase() },
  { key: "height_cm",      label: "H (cm)",      get: (s) => (s.height_cm ?? s.snapshot?.height_cm ?? -1) },
  { key: "weight_kg",      label: "W (kg)",      get: (s) => (s.weight_kg ?? s.snapshot?.weight_kg ?? -1) },
  { key: "gps",            label: "GPS",         get: (s) => (s.gps ?? -1) },
  { key: "archetype",      label: "Archetype",   get: (s) => (s.archetype?.code || "") },
  { key: "verified",       label: "Status",      get: (s) => (s.verified ? 1 : 0) },
  { key: null,             label: "Card",        get: () => 0 },
  { key: null,             label: "Action",      get: () => 0 },
];

const GYMS_COLS = [
  { key: "name",     label: "Institution", get: (g) => (g.name || "").toLowerCase() },
  { key: "athletes", label: "Athletes",    get: (g) => (g.athletes || 0) },
  { key: "status",   label: "Status",      get: (g) => (g.pending_approval ? 0 : g.listed ? 1 : 2) },
  { key: null,       label: "Action",      get: () => 0 },
];

const EVENTS_COLS = [
  { key: "date",             label: "Date",       get: (e) => (e.date || "") },
  { key: "name",             label: "Name",       get: (e) => (e.name || "").toLowerCase() },
  { key: "location",         label: "Location",   get: (e) => (e.location || "").toLowerCase() },
  { key: "status",           label: "Status",     get: (e) => (e.status || "") },
  { key: "interested_count", label: "Interested", get: (e) => (e.interested_count || 0) },
  { key: null,               label: "Actions",    get: () => 0 },
];

const RESETS_COLS = [
  { key: "created_at",     label: "Requested", get: (r) => (r.created_at || "") },
  { key: "user_name",      label: "Athlete",   get: (r) => (r.user_name || "").toLowerCase() },
  { key: "user_email",     label: "Contact",   get: (r) => (r.user_email || "").toLowerCase() },
  { key: "temp_password",  label: "Temp Code", get: (r) => (r.temp_password ? 1 : 0) },
  { key: "status",         label: "Status",    get: (r) => (r.status || "") },
  { key: null,             label: "Action",    get: () => 0 },
];
import { CheckCircle2, XCircle, Save, Download, Upload, Trash2, Calendar, Plus, Users as UsersIcon, X, ShieldCheck, KeyRound, Activity, Pencil, ArrowUp, ArrowDown, ArrowUpDown, Settings as SettingsIcon, Mail, Phone, ChevronLeft, ChevronRight, Search, UserPlus, Trophy } from "lucide-react";

// Reusable sort hook — picks one column key + direction, returns a sorted shallow copy.
function useSort(rows, cols, defaultDir = "asc") {
  const [sort, setSort] = useState({ key: null, dir: defaultDir });
  const toggle = useCallback((key) => {
    if (!key) return;
    setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: defaultDir });
  }, [defaultDir]);
  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const col = cols.find((c) => c.key === sort.key);
    if (!col) return rows;
    return [...rows].sort((a, b) => {
      const va = col.get(a);
      const vb = col.get(b);
      if (va < vb) return sort.dir === "asc" ? -1 : 1;
      if (va > vb) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sort, cols]);
  return { sort, toggle, sorted };
}

// Reusable sortable <th> renderer.
function SortableTh({ col, sort, onToggle, testIdPrefix }) {
  const active = col.key && sort.key === col.key;
  return (
    <th
      className={`gps-heading text-xs tracking-[0.22em] text-mute font-medium uppercase text-left p-3 select-none ${col.key ? "cursor-pointer hover:text-white" : ""}`}
      onClick={() => col.key && onToggle(col.key)}
      data-testid={col.key ? `${testIdPrefix}-${col.key}` : undefined}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {col.label}
      {col.key && (
        active
          ? (sort.dir === "asc"
              ? <ArrowUp size={11} className="inline ml-1 text-cyan-400" />
              : <ArrowDown size={11} className="inline ml-1 text-cyan-400" />)
          : <ArrowUpDown size={11} className="inline ml-1 opacity-40" />
      )}
    </th>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  // Server-side pagination for USERS
  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(10);
  const [usersTotal, setUsersTotal] = useState(0);
  // Server-side pagination for the other admin tabs
  const [activePage, setActivePage] = useState(1);
  const [activePageSize, setActivePageSize] = useState(10);
  const [activeTotal, setActiveTotal] = useState(0);
  const [scoresPage, setScoresPage] = useState(1);
  const [scoresPageSize, setScoresPageSize] = useState(10);
  const [scoresTotal, setScoresTotal] = useState(0);
  const [gymsPage, setGymsPage] = useState(1);
  const [gymsPageSize, setGymsPageSize] = useState(10);
  const [gymsTotal, setGymsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [eventsPageSize, setEventsPageSize] = useState(10);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [pwResetsPage, setPwResetsPage] = useState(1);
  const [pwResetsPageSize, setPwResetsPageSize] = useState(10);
  const [pwResetsTotal, setPwResetsTotal] = useState(0);
  const [gyms, setGyms] = useState([]);
  const [tests, setTests] = useState([]);
  const [testEdits, setTestEdits] = useState({}); // {key: {qdr_M: [], qdr_F: []}}
  const [query, setQuery] = useState("");
  // Global admin search — searches users + institutions from any tab
  const [globalQ, setGlobalQ] = useState("");
  const [globalResults, setGlobalResults] = useState(null); // {users, institutions} | null
  const [globalOpen, setGlobalOpen] = useState(false);
  const globalRef = useRef(null);
  const [edit, setEdit] = useState({}); // {userId: patch}
  const [addAthleteOpen, setAddAthleteOpen] = useState(false);
  const emptyNewAthlete = {
    name: "", email: "", phone: "", gender: "M", dob: "",
    country: "", city: "", athlete_class: "regular", primary_sport: "", gym: "",
  };
  const [newAthlete, setNewAthlete] = useState(emptyNewAthlete);
  const [addBusy, setAddBusy] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [newGymName, setNewGymName] = useState("");
  const [gymEdits, setGymEdits] = useState({}); // { [oldName]: newName }
  const importRef = React.useRef(null);
  // Events tab state
  const [events, setEvents] = useState([]);
  const emptyEvent = { id: "", name: "", date: "", location: "", summary: "", status: "upcoming", image_url: "", registration_url: "" };
  const [eventForm, setEventForm] = useState(emptyEvent);
  const [interestModal, setInterestModal] = useState(null); // {event, athletes}
  const [scoringEvent, setScoringEvent] = useState(null); // event being scored, or null
  const [scoreDialog, setScoreDialog] = useState(null); // {mode:'create'|'edit', score?}
  const [volunteerDialog, setVolunteerDialog] = useState(null); // target user or null
  // Scores tab state
  const [scores, setScores] = useState([]);
  const [scoresFilter, setScoresFilter] = useState("pending"); // pending|verified|all
  const [scoresQuery, setScoresQuery] = useState("");
  const [scoresSearch, setScoresSearch] = useState(""); // debounced value used in the request
  // Active Users tab state
  const [activeWindow, setActiveWindow] = useState(1); // days: 1|7|30
  const [activeData, setActiveData] = useState({ active: [], dau: 0, wau: 0, mau: 0 });
  // Password Resets tab state
  const [pwResets, setPwResets] = useState([]);
  const [pwResetFilter, setPwResetFilter] = useState("pending"); // pending|issued|cancelled|all
  const [sports, setSports] = useState(SPORTS_FALLBACK);
  useEffect(() => {
    api.get("/sports").then((r) => setSports(r.data.sports || SPORTS_FALLBACK)).catch(() => {});
  }, []);
  // Users selection (for bulk CSV export)
  const [selectedUsers, setSelectedUsers] = useState(() => new Set());
  const toggleUserSelected = (uid) => {
    setSelectedUsers((s) => {
      const c = new Set(s);
      if (c.has(uid)) c.delete(uid); else c.add(uid);
      return c;
    });
  };
  // GPS card preview state for the Scores tab
  const [cardPayload, setCardPayload] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const cardRef = useRef(null);
  // Users sort state — uses shared useSort hook.
  const { sort: userSort, toggle: toggleUserSort, sorted: sortedUsers } = useSort(users, USERS_COLS);
  // Sort hooks for the other 5 sortable tabs (active users, scores, gyms, events, password resets).
  const { sort: activeSort, toggle: toggleActiveSort, sorted: sortedActive } = useSort(activeData.active || [], ACTIVE_COLS, "desc");
  const { sort: scoresSort, toggle: toggleScoresSort, sorted: sortedScores } = useSort(scores, SCORES_COLS, "desc");
  const { sort: gymsSort, toggle: toggleGymsSort, sorted: sortedGyms } = useSort(gyms, GYMS_COLS);
  const { sort: eventsSort, toggle: toggleEventsSort, sorted: sortedEvents } = useSort(events, EVENTS_COLS, "desc");
  const { sort: resetsSort, toggle: toggleResetsSort, sorted: sortedResets } = useSort(pwResets, RESETS_COLS, "desc");
  // Settings tab state
  const [settings, setSettings] = useState({ contact_email: "", contact_phone: "" });
  const [settingsBusy, setSettingsBusy] = useState(false);
  const loadSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings({
        contact_email: data?.contact_email || "",
        contact_phone: data?.contact_phone || "",
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, []);
  const saveSettings = async () => {
    setSettingsBusy(true);
    try {
      const { data } = await api.patch("/admin/settings", {
        contact_email: settings.contact_email.trim(),
        contact_phone: settings.contact_phone.trim(),
      });
      setSettings({
        contact_email: data?.contact_email || "",
        contact_phone: data?.contact_phone || "",
      });
      toast.success("CONTACT SETTINGS SAVED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSettingsBusy(false);
    }
  };

  const exportCsv = async () => {
    try {
      const ids = Array.from(selectedUsers);
      const url = ids.length
        ? `/admin/athletes/export?ids=${encodeURIComponent(ids.join(","))}`
        : "/admin/athletes/export";
      const res = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      const tag = ids.length ? `${ids.length}users` : "all";
      a.download = `gps-athletes-${tag}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(ids.length ? `EXPORTED ${ids.length} SELECTED` : "ATHLETES CSV EXPORTED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const exportScoresCsv = async (filter = "all") => {
    try {
      const res = await api.get(`/admin/scores/export?filter=${encodeURIComponent(filter)}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `gps-scores-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`EXPORTED ${filter.toUpperCase()} SCORES`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openScoreCard = (s) => {
    setCardPayload({
      name: s.owner?.name || "ATHLETE",
      avatarUrl: s.owner?.avatar_url || null,
      date: s.created_at ? new Date(s.created_at) : new Date(),
      gps: s.gps || 0,
      archetype: s.archetype,
      pillars: s.pillars || {},
      country: s.owner?.country || "",
      gym: s.owner?.gym || "",
      verified: !!s.verified,
      publicId: s.owner?.public_id || "",
      scoreId: s.id,
    });
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setCardBusy(true);
    try {
      // Base card is 540×675 (4:5). pixelRatio=2 yields exactly 1080×1350.
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#000000",
        skipFonts: true,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      const tag = (cardPayload?.name || "ATHLETE").replace(/[^A-Za-z0-9]+/g, "_");
      a.download = `gps-card-${tag}-${(cardPayload?.scoreId || "").slice(0, 8)}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("CARD DOWNLOADED");
    } catch (err) {
      toast.error(err.message || "Card export failed");
    } finally {
      setCardBusy(false);
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("File must be a .csv");
      return;
    }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/athletes/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`CSV IMPORTED · ${data.created} created · ${data.updated} updated · ${data.scored} scored`);
      if (data.errors?.length) {
        toast.error(`${data.errors.length} row error(s) — check console`);
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("CSV import errors:", data.errors);
        }
      }
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setImportBusy(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const loadUsers = useCallback(async () => {
    const { data } = await api.get(
      `/admin/users?q=${encodeURIComponent(query)}&page=${usersPage}&page_size=${usersPageSize}`
    );
    setUsers(data.users || []);
    setUsersTotal(data.total || 0);
  }, [query, usersPage, usersPageSize]);

  const loadGyms = useCallback(async () => {
    const { data } = await api.get(`/admin/gyms?page=${gymsPage}&page_size=${gymsPageSize}`);
    setGyms(data.gyms || []);
    setGymsTotal(data.total || 0);
  }, [gymsPage, gymsPageSize]);

  const loadTests = useCallback(async () => {
    const { data } = await api.get("/admin/tests");
    setTests(data.tests || []);
  }, []);

  const loadEvents = useCallback(async () => {
    const { data } = await api.get(`/events?page=${eventsPage}&page_size=${eventsPageSize}`);
    setEvents(data.events || []);
    setEventsTotal(data.total || 0);
  }, [eventsPage, eventsPageSize]);

  const loadScores = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(scoresPage),
      page_size: String(scoresPageSize),
    });
    if (scoresFilter !== "all") params.set("verified", String(scoresFilter === "verified"));
    if (scoresSearch.trim().length >= 2) params.set("q", scoresSearch.trim());
    const { data } = await api.get(`/admin/scores?${params.toString()}`);
    setScores(data.scores || []);
    setScoresTotal(data.total || 0);
  }, [scoresFilter, scoresPage, scoresPageSize, scoresSearch]);

  const loadActive = useCallback(async () => {
    const { data } = await api.get(
      `/admin/active-users?days=${activeWindow}&page=${activePage}&page_size=${activePageSize}`
    );
    setActiveData(data || { active: [], dau: 0, wau: 0, mau: 0 });
    setActiveTotal(data?.total || 0);
  }, [activeWindow, activePage, activePageSize]);

  const loadPwResets = useCallback(async () => {
    const base = `/admin/password-resets?page=${pwResetsPage}&page_size=${pwResetsPageSize}`;
    const url = pwResetFilter === "all" ? base : `${base}&status=${pwResetFilter}`;
    const { data } = await api.get(url);
    setPwResets(data.requests || []);
    setPwResetsTotal(data.total || 0);
  }, [pwResetFilter, pwResetsPage, pwResetsPageSize]);

  // Reset each tab's page to 1 whenever its filter changes so we don't get stuck
  // on an out-of-range page (e.g., viewing p3 of pending, then flipping to verified).
  useEffect(() => { setScoresPage(1); }, [scoresFilter, scoresSearch]);
  useEffect(() => { setActivePage(1); }, [activeWindow]);
  useEffect(() => { setPwResetsPage(1); }, [pwResetFilter]);
  useEffect(() => { setUsersPage(1); }, [query]);

  // Debounce the Scores tab search input so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setScoresSearch(scoresQuery), 250);
    return () => clearTimeout(t);
  }, [scoresQuery]);

  // Global search — debounced fetch when input has 2+ chars
  useEffect(() => {
    const q = globalQ.trim();
    if (q.length < 2) { setGlobalResults(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/admin/search?q=${encodeURIComponent(q)}`);
        setGlobalResults(data);
      } catch (err) {
        setGlobalResults(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [globalQ]);
  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (ev) => {
      if (globalRef.current && !globalRef.current.contains(ev.target)) setGlobalOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  // Handlers to jump to the correct tab with the filter pre-set
  const jumpToUser = (u) => {
    setTab("users");
    setQuery(u.public_id || u.email || u.name || "");
    setGlobalOpen(false);
    setGlobalQ("");
  };
  const jumpToInstitution = (g) => {
    setTab("users");
    setQuery(g.name);
    setGlobalOpen(false);
    setGlobalQ("");
  };

  useEffect(() => {
    if (user?.role === "admin") {
      if (tab === "users") loadUsers();
      else if (tab === "gyms") loadGyms();
      else if (tab === "tests") loadTests();
      else if (tab === "events") loadEvents();
      else if (tab === "scores") loadScores();
      else if (tab === "active") loadActive();
      else if (tab === "resets") loadPwResets();
      else if (tab === "settings") loadSettings();
    }
  }, [user, tab, loadUsers, loadGyms, loadTests, loadEvents, loadScores, loadActive, loadPwResets, loadSettings]);

  const downloadActiveCsv = async () => {
    try {
      const res = await api.get(`/admin/active-users.csv?days=${activeWindow}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gps-active-users-${activeWindow}d-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("ACTIVE USERS CSV EXPORTED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const markSent = async (req) => {
    try {
      await api.post(`/admin/password-resets/${req.id}/complete`);
      setPwResets((rows) => rows.map((x) => x.id === req.id ? {
        ...x, status: "issued",
        issued_at: new Date().toISOString(),
      } : x));
      toast.success("MARKED AS SENT");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const cancelPwReset = async (req) => {
    try {
      await api.post(`/admin/password-resets/${req.id}/cancel`);
      setPwResets((rows) => rows.map((x) => x.id === req.id ? { ...x, status: "cancelled" } : x));
      toast.success("REQUEST DISMISSED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const verifyScore = async (s, verified) => {
    try {
      await api.post(`/admin/scores/${s.id}/verify`, { verified });
      toast.success(`SCORE ${verified ? "VERIFIED" : "UNVERIFIED"}`);
      loadScores();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteScore = async (s) => {
    const label = s.snapshot?.name || s.owner?.name || "this athlete";
    const dateStr = s.created_at ? new Date(s.created_at).toLocaleDateString() : "";
    if (!window.confirm(
      `Permanently delete ${label}'s score from ${dateStr}?\n\n` +
      `GPS ${s.gps?.toFixed?.(1) ?? "—"} · ${s.archetype?.code || ""}\n\n` +
      "This will refresh their Open + Pro snapshots. Cannot be undone."
    )) return;
    try {
      await api.delete(`/admin/scores/${s.id}`);
      toast.success("SCORE DELETED");
      loadScores();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="text-center py-24 text-mute gps-mono">LOADING...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const setEditField = (uid, key, value) => {
    setEdit((s) => ({ ...s, [uid]: { ...(s[uid] || {}), [key]: value } }));
  };

  const saveUser = async (u) => {
    const patch = edit[u.id];
    if (!patch || Object.keys(patch).length === 0) return;
    try {
      await api.patch(`/admin/users/${u.id}`, patch);
      toast.success(`${u.name}: updated`);
      setEdit((s) => ({ ...s, [u.id]: {} }));
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name} (${u.email}) and all their scans?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const createAthlete = async () => {
    const name = newAthlete.name.trim();
    const email = newAthlete.email.trim();
    const phone = newAthlete.phone.trim();
    const gender = (newAthlete.gender || "M").toUpperCase();
    const dob = (newAthlete.dob || "").trim();
    const country = (newAthlete.country || "").trim();
    const city = (newAthlete.city || "").trim();
    const athlete_class = (newAthlete.athlete_class || "").trim();
    const primary_sport = (newAthlete.primary_sport || "").trim();
    const gym = (newAthlete.gym || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    if (!email) { toast.error("Email is required"); return; }
    if (!phone) { toast.error("Phone is required"); return; }
    if (!["M", "F"].includes(gender)) {
      toast.error("Gender must be M or F");
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) {
      toast.error("Date of birth is required (YYYY-MM-DD)");
      return;
    }
    if (!/^[+0-9\s\-()]{6,20}$/.test(phone)) {
      toast.error("Phone must be 6-20 chars (digits, +, spaces, dashes, parentheses)");
      return;
    }
    if (!country) { toast.error("Country is required"); return; }
    if (!city) { toast.error("City is required"); return; }
    if (!ATHLETE_CLASSES.some((c) => c.value === athlete_class)) {
      toast.error(`Athlete class must be one of: ${ATHLETE_CLASSES.map((c) => c.value).join(", ")}`);
      return;
    }
    if (!primary_sport) { toast.error("Primary sport is required"); return; }
    if (!gym) { toast.error("Institution / gym is required"); return; }
    setAddBusy(true);
    try {
      const res = await api.post(`/admin/users`, {
        name, email, phone, gender, dob,
        country, city, athlete_class, primary_sport, gym,
      });
      const initialPw = res.data?.initial_password || "";
      const canonicalEmail = res.data?.user?.email || email;
      // Persistent toast so admin can copy the exact credentials.
      toast.success(
        `${name} onboarded — login: ${canonicalEmail} / password: ${initialPw}`,
        { duration: 30000 }
      );
      setAddAthleteOpen(false);
      setNewAthlete(emptyNewAthlete);
      loadUsers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAddBusy(false);
    }
  };

  const toggleGymListed = async (g) => {
    try {
      await api.patch(`/admin/gyms/${encodeURIComponent(g.name)}`, { name: g.name, listed: !g.listed });
      toast.success(`${g.name}: ${!g.listed ? "LISTED" : "DELISTED"}`);
      loadGyms();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const addGym = async (e) => {
    e?.preventDefault?.();
    const name = newGymName.trim();
    if (!name) { toast.error("Gym name required"); return; }
    try {
      await api.post("/admin/gyms", { name, listed: true });
      toast.success(`${name} ADDED`);
      setNewGymName("");
      loadGyms();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteGym = async (g) => {
    const msg = g.athletes > 0
      ? `Delete ${g.name}? This will UNAFFILIATE ${g.athletes} athlete${g.athletes === 1 ? "" : "s"} currently tied to this gym.`
      : `Delete ${g.name}?`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.delete(`/admin/gyms/${encodeURIComponent(g.name)}`);
      toast.success(`${g.name} deleted · ${data.unaffiliated_athletes} unaffiliated`);
      loadGyms();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const approveGym = async (g) => {
    try {
      await api.post(`/admin/gyms/${encodeURIComponent(g.name)}/approve`);
      toast.success(`${g.name} APPROVED & LISTED`);
      loadGyms();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const saveGymRename = async (g) => {
    const newName = (gymEdits[g.name] || "").trim();
    if (!newName) { toast.error("New name required"); return; }
    if (newName === g.name) {
      setGymEdits((s) => { const c = { ...s }; delete c[g.name]; return c; });
      return;
    }
    const collides = gyms.some((x) => x.name === newName && x.name !== g.name);
    const msg = collides
      ? `"${newName}" already exists. Merge "${g.name}" into "${newName}"? All ${g.athletes} affiliated athlete${g.athletes === 1 ? "" : "s"} will be moved to "${newName}".`
      : `Rename "${g.name}" to "${newName}"? This updates ${g.athletes} affiliated profile${g.athletes === 1 ? "" : "s"}.`;
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.post(
        `/admin/gyms/${encodeURIComponent(g.name)}/rename`,
        { new_name: newName },
      );
      toast.success(
        data.merged
          ? `Merged into ${newName} · ${data.renamed_users} profiles updated`
          : `Renamed to ${newName} · ${data.renamed_users} profiles updated`
      );
      setGymEdits((s) => { const c = { ...s }; delete c[g.name]; return c; });
      loadGyms();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  // ---- Events ----
  const setEventField = (k, v) => setEventForm((f) => ({ ...f, [k]: v }));

  const editEvent = (e) => {
    setEventForm({
      id: e.id, name: e.name, date: e.date, location: e.location,
      summary: e.summary || "", status: e.status || "upcoming",
      image_url: e.image_url || "", registration_url: e.registration_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveEvent = async (ev) => {
    ev?.preventDefault?.();
    const body = {
      name: eventForm.name.trim(),
      date: eventForm.date,
      location: eventForm.location.trim(),
      summary: eventForm.summary.trim(),
      status: eventForm.status,
      image_url: eventForm.image_url.trim() || null,
      registration_url: eventForm.registration_url.trim() || null,
    };
    if (!body.name || !body.date || !body.location) {
      toast.error("Name, date and location are required");
      return;
    }
    try {
      if (eventForm.id) {
        await api.patch(`/admin/events/${eventForm.id}`, body);
        toast.success(`${body.name}: updated`);
      } else {
        await api.post("/admin/events", body);
        toast.success(`${body.name}: created`);
      }
      setEventForm(emptyEvent);
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteEvent = async (e) => {
    if (!window.confirm(`Delete event "${e.name}"? Also clears all interest records.`)) return;
    try {
      await api.delete(`/admin/events/${e.id}`);
      toast.success(`${e.name}: deleted`);
      loadEvents();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const viewInterested = async (e) => {
    try {
      const { data } = await api.get(`/admin/events/${e.id}/interested`);
      setInterestModal({ event: data.event, athletes: data.athletes });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadInterestCsv = async (e) => {
    try {
      const res = await api.get(`/admin/events/${e.id}/interested.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gps-event-${e.name.replace(/[^A-Za-z0-9]+/g, "_")}-interested.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV DOWNLOADED");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const setTestQDR = (key, gender, idx, value) => {
    setTestEdits((s) => {
      const t = tests.find((x) => x.key === key);
      if (!t) return s;
      const base = s[key]?.[`qdr_${gender}`] || [...t[`qdr_${gender}`]];
      const next = [...base];
      next[idx] = value;
      return { ...s, [key]: { ...(s[key] || {}), [`qdr_${gender}`]: next } };
    });
  };

  const saveTest = async (t) => {
    const patch = testEdits[t.key];
    if (!patch) return;
    const payload = {};
    if (patch.qdr_M) payload.qdr_M = patch.qdr_M.map(parseFloat);
    if (patch.qdr_F) payload.qdr_F = patch.qdr_F.map(parseFloat);
    try {
      await api.patch(`/admin/tests/${t.key}`, payload);
      toast.success(`${t.name}: QDR updated`);
      setTestEdits((s) => { const c = { ...s }; delete c[t.key]; return c; });
      loadTests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const resetTest = async (t) => {
    try {
      await api.delete(`/admin/tests/${t.key}`);
      toast.success(`${t.name}: reset to default`);
      setTestEdits((s) => { const c = { ...s }; delete c[t.key]; return c; });
      loadTests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="admin-page">
      <div className="flex flex-col md:flex-row md:items-center gap-6 mt-2">
        <div>
          <div className="gps-tag mb-3">ADMIN CONSOLE</div>
          <h1 className="gps-heading text-4xl md:text-5xl">GPS COMMAND</h1>
        </div>
        <div className="flex-1" />
        {/* Global search bar */}
        <div ref={globalRef} className="relative w-full md:w-96" data-testid="admin-global-search">
          <div className="flex items-center gap-2 gps-input pl-3">
            <Search size={14} className="text-mute" />
            <input
              type="text"
              className="flex-1 bg-transparent outline-none border-0 py-2 gps-mono text-xs tracking-[0.15em] text-white placeholder:text-mute"
              placeholder="SEARCH ATHLETES · INSTITUTIONS..."
              value={globalQ}
              onFocus={() => setGlobalOpen(true)}
              onChange={(e) => { setGlobalQ(e.target.value); setGlobalOpen(true); }}
              data-testid="admin-global-search-input"
            />
            {globalQ && (
              <button
                type="button"
                onClick={() => { setGlobalQ(""); setGlobalResults(null); }}
                className="text-mute hover:text-white"
                aria-label="Clear search"
                data-testid="admin-global-search-clear"
              >
                <X size={12} />
              </button>
            )}
          </div>
          {globalOpen && globalResults && (globalResults.users.length + globalResults.institutions.length > 0 ? (
            <div
              className="absolute right-0 mt-2 w-full md:w-[28rem] z-50 border border-white/15 rounded-md bg-black/95 backdrop-blur-sm shadow-2xl overflow-hidden"
              data-testid="admin-global-search-results"
            >
              {globalResults.users.length > 0 && (
                <>
                  <div className="px-3 py-2 text-[10px] gps-mono tracking-[0.25em] text-mute border-b border-white/10">
                    ATHLETES · {globalResults.users.length}
                  </div>
                  {globalResults.users.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => jumpToUser(u)}
                      className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 flex items-center gap-3"
                      data-testid={`admin-global-user-${u.id}`}
                    >
                      <div className="gps-mono text-[10px] text-cyan-400 tracking-[0.15em] w-24 shrink-0 truncate">
                        {u.public_id || "—"}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{u.name}</div>
                        <div className="gps-mono text-[10px] text-mute truncate">
                          {u.email}{u.gym ? ` · ${u.gym}` : ""}
                        </div>
                      </div>
                      {u.athlete_class === "pro" && (
                        <span className="gps-tag" style={{ borderColor: "rgba(0,240,255,0.6)", color: "#00f0ff", fontSize: 9 }}>PRO</span>
                      )}
                    </button>
                  ))}
                </>
              )}
              {globalResults.institutions.length > 0 && (
                <>
                  <div className="px-3 py-2 text-[10px] gps-mono tracking-[0.25em] text-mute border-b border-white/10 bg-white/[0.02]">
                    INSTITUTIONS · {globalResults.institutions.length}
                  </div>
                  {globalResults.institutions.map((g) => (
                    <button
                      key={g.name}
                      onClick={() => jumpToInstitution(g)}
                      className="w-full text-left px-3 py-2 hover:bg-white/5 border-b border-white/5 flex items-center gap-3"
                      data-testid={`admin-global-inst-${g.name}`}
                    >
                      <UsersIcon size={12} className="text-mute" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{g.name}</div>
                        <div className="gps-mono text-[10px] text-mute">
                          {g.athletes} ATHLETE{g.athletes === 1 ? "" : "S"}
                          {g.pending_approval ? " · PENDING" : g.listed ? "" : " · DELISTED"}
                        </div>
                      </div>
                    </button>
                  ))}
                </>
              )}
            </div>
          ) : (
            globalQ.trim().length >= 2 && (
              <div
                className="absolute right-0 mt-2 w-full md:w-[28rem] z-50 border border-white/15 rounded-md bg-black/95 backdrop-blur-sm px-3 py-4 text-center gps-mono text-xs text-mute tracking-[0.2em]"
                data-testid="admin-global-search-empty"
              >
                NO MATCHES
              </div>
            )
          ))}
        </div>
      </div>

      <div className="flex gap-2 mt-8 flex-wrap">
        <button className={`gps-btn ${tab === "users" ? "" : "ghost"}`} onClick={() => setTab("users")} data-testid="admin-tab-users">USERS</button>
        <button className={`gps-btn ${tab === "active" ? "" : "ghost"}`} onClick={() => setTab("active")} data-testid="admin-tab-active">ACTIVE USERS</button>
        <button className={`gps-btn ${tab === "scores" ? "" : "ghost"}`} onClick={() => setTab("scores")} data-testid="admin-tab-scores">SCORES</button>
        <button className={`gps-btn ${tab === "gyms" ? "" : "ghost"}`} onClick={() => setTab("gyms")} data-testid="admin-tab-gyms">INSTITUTIONS</button>
        <button className={`gps-btn ${tab === "tests" ? "" : "ghost"}`} onClick={() => setTab("tests")} data-testid="admin-tab-tests">TESTS · QDR</button>
        <button className={`gps-btn ${tab === "events" ? "" : "ghost"}`} onClick={() => setTab("events")} data-testid="admin-tab-events">EVENTS</button>
        <button className={`gps-btn ${tab === "resets" ? "" : "ghost"}`} onClick={() => setTab("resets")} data-testid="admin-tab-resets">PASSWORD RESETS</button>
        <button className={`gps-btn ${tab === "settings" ? "" : "ghost"}`} onClick={() => setTab("settings")} data-testid="admin-tab-settings"><SettingsIcon size={12} /> SETTINGS</button>
      </div>

      {tab === "users" && (
        <>
          <GlassPanel className="mt-6 p-4 flex flex-wrap gap-3 items-center" data-testid="admin-csv-bar">
            <div>
              <div className="gps-label">ATHLETE DATA</div>
              <div className="text-mute text-xs">
                Export the full athlete roster + latest scores, edit offline, then re-upload to apply changes.
                Tick the rows you want to export only those, or leave all unticked to export everyone.
              </div>
            </div>
            <div className="flex-1" />
            <button
              onClick={() => { setNewAthlete(emptyNewAthlete); setAddAthleteOpen(true); }}
              className="gps-btn"
              data-testid="admin-add-athlete-btn"
            >
              <UserPlus size={14} /> ADD ATHLETE
            </button>
            <input
              ref={importRef}
              type="file"
              accept=".csv,text/csv"
              onChange={onImportFile}
              className="hidden"
              data-testid="admin-csv-file"
            />
            <button onClick={exportCsv} className="gps-btn ghost" data-testid="admin-csv-export">
              <Download size={14} /> EXPORT CSV
              {selectedUsers.size > 0 && (
                <span className="ml-1 text-cyan-400">· {selectedUsers.size} SELECTED</span>
              )}
            </button>
            <button
              onClick={() => importRef.current?.click()}
              disabled={importBusy}
              className="gps-btn"
              data-testid="admin-csv-import"
            >
              <Upload size={14} /> {importBusy ? "IMPORTING..." : "UPLOAD CSV"}
            </button>
          </GlassPanel>

          <GlassPanel className="mt-4 p-4 flex gap-3 items-center">
            <input
              className="gps-input flex-1"
              placeholder="Search by name, email, phone, or ID..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="admin-user-search"
            />
            <button className="gps-btn" onClick={loadUsers} data-testid="admin-search-btn">SEARCH</button>
          </GlassPanel>

          <GlassPanel className="mt-4 overflow-x-auto">
            <table className="w-full text-sm min-w-[2000px] border-collapse">
              <thead>
                <tr className="border-b border-white/15">
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      data-testid="admin-users-select-all"
                      aria-label="Select all athletes on this page"
                      checked={sortedUsers.length > 0 && sortedUsers.filter((u) => u.role !== "admin").every((u) => selectedUsers.has(u.id))}
                      onChange={(ev) => {
                        const ids = sortedUsers.filter((u) => u.role !== "admin").map((u) => u.id);
                        if (ev.target.checked) {
                          setSelectedUsers(new Set([...selectedUsers, ...ids]));
                        } else {
                          const next = new Set(selectedUsers);
                          ids.forEach((id) => next.delete(id));
                          setSelectedUsers(next);
                        }
                      }}
                      className="accent-cyan-400 cursor-pointer"
                    />
                  </th>
                  {USERS_COLS.map((c) => (
                    <SortableTh
                      key={c.key || c.label}
                      col={c}
                      sort={userSort}
                      onToggle={toggleUserSort}
                      testIdPrefix="admin-users-sort"
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedUsers.map((u) => {
                  const e = edit[u.id] || {};
                  const isAdmin = u.role === "admin";
                  return (
                    <tr key={u.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="p-3 w-10 align-middle">
                        {!isAdmin && (
                          <input
                            type="checkbox"
                            data-testid={`admin-users-select-${u.id}`}
                            aria-label={`Select ${u.name}`}
                            checked={selectedUsers.has(u.id)}
                            onChange={() => toggleUserSelected(u.id)}
                            className="accent-cyan-400 cursor-pointer"
                          />
                        )}
                      </td>
                      <td className="p-3 gps-mono text-xs text-cyan-400 tracking-[0.2em]" data-testid={`admin-user-pid-${u.id}`}>
                        {u.public_id || "—"}
                      </td>
                      <td className="p-3 min-w-[200px]"><input className="gps-input w-full" defaultValue={u.name} onChange={(ev) => setEditField(u.id, "name", ev.target.value)} /></td>
                      <td className="p-3 min-w-[220px]">
                        <input
                          type="email"
                          className="gps-input w-full gps-mono text-xs"
                          defaultValue={u.email || ""}
                          onChange={(ev) => setEditField(u.id, "email", ev.target.value)}
                          data-testid={`admin-user-email-${u.id}`}
                        />
                      </td>
                      <td className="p-3 min-w-[160px]">
                        <input
                          type="tel"
                          className="gps-input w-full gps-mono text-xs"
                          pattern="[+0-9\s\-()]{6,20}"
                          defaultValue={u.phone || ""}
                          onChange={(ev) => setEditField(u.id, "phone", ev.target.value)}
                          data-testid={`admin-user-phone-${u.id}`}
                        />
                      </td>
                      <td className="p-3">
                        <select className="gps-input" defaultValue={u.gender || "M"} onChange={(ev) => setEditField(u.id, "gender", ev.target.value)}>
                          <option value="M">M</option><option value="F">F</option>
                        </select>
                      </td>
                      <td className="p-3">
                        <input
                          type="date"
                          className="gps-input"
                          defaultValue={(u.dob || "").slice(0, 10)}
                          onChange={(ev) => setEditField(u.id, "dob", ev.target.value)}
                          data-testid={`admin-user-dob-${u.id}`}
                        />
                      </td>
                      <td className="p-3 gps-mono text-xs text-white text-center whitespace-nowrap" data-testid={`admin-user-age-${u.id}`}>
                        {u.age ?? "—"}
                      </td>
                      <td className="p-3">
                        <select
                          className="gps-input"
                          defaultValue={u.athlete_class || "regular"}
                          onChange={(ev) => setEditField(u.id, "athlete_class", ev.target.value)}
                          data-testid={`admin-user-class-${u.id}`}
                        >
                          {ATHLETE_CLASSES.map((c) => (
                            <option key={c.value} value={c.value}>{c.value.toUpperCase()}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3">
                        <select
                          className="gps-input"
                          defaultValue={u.primary_sport || ""}
                          onChange={(ev) => setEditField(u.id, "primary_sport", ev.target.value)}
                          data-testid={`admin-user-sport-${u.id}`}
                        >
                          <option value="">—</option>
                          {sports.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-3 min-w-[140px]"><input className="gps-input w-full" defaultValue={u.country || ""} onChange={(ev) => setEditField(u.id, "country", ev.target.value)} /></td>
                      <td className="p-3 min-w-[140px]"><input className="gps-input w-full" defaultValue={u.city || ""} onChange={(ev) => setEditField(u.id, "city", ev.target.value)} /></td>
                      <td className="p-3 min-w-[220px]"><input className="gps-input w-full" defaultValue={u.gym || ""} onChange={(ev) => setEditField(u.id, "gym", ev.target.value)} /></td>
                      <td className="p-3 gps-mono text-xs text-mute whitespace-nowrap" data-testid={`admin-user-joined-${u.id}`}>
                        {JOINED_FMT(u.created_at)}
                      </td>
                      <td className="p-3 big-numeric text-white">
                        <div>{u.current_gps?.toFixed?.(1) ?? "—"}</div>
                        <div className="gps-mono text-[10px] text-cyan mt-1">
                          PRO: {u.current_gps_verified?.toFixed?.(1) ?? "—"}
                        </div>
                      </td>
                      <td className="p-3 gps-mono text-xs whitespace-nowrap" data-testid={`admin-user-role-${u.id}`}>
                        {u.role === "admin" ? (
                          <span className="text-rose-400">ADMIN</span>
                        ) : (
                          <span className="text-mute">ATHLETE</span>
                        )}
                        {u.is_volunteer && (
                          <span className="text-cyan-400 block mt-1" data-testid={`admin-user-volunteer-tag-${u.id}`}>
                            + VOLUNTEER · {(u.assigned_event_ids || []).length} EVT
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="flex gap-1 flex-wrap">
                          <button onClick={() => saveUser(u)} className="gps-btn ghost" disabled={!e || Object.keys(e).length === 0} data-testid={`admin-save-${u.id}`}>
                            <Save size={12} /> SAVE
                          </button>
                          {!isAdmin && (
                            <button
                              onClick={() => setVolunteerDialog(u)}
                              className="gps-btn ghost"
                              data-testid={`admin-volunteer-${u.id}`}
                              aria-label={u.is_volunteer ? "Manage volunteer" : "Make volunteer"}
                              title={u.is_volunteer ? "Manage volunteer assignments" : "Flag this athlete as a volunteer"}
                            >
                              <ShieldCheck size={12} /> MANAGE VOL
                            </button>
                          )}
                          {!isAdmin && (
                            <button
                              onClick={() => deleteUser(u)}
                              className="gps-btn ghost"
                              data-testid={`admin-delete-${u.id}`}
                              aria-label="Delete user"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {sortedUsers.length === 0 && (
                  <tr><td colSpan={USERS_COLS.length + 1} className="p-8 text-center text-mute gps-mono text-xs">NO USERS</td></tr>
                )}
              </tbody>
            </table>
          </GlassPanel>

          {/* Pagination bar */}
          <PaginationBar
            page={usersPage}
            pageSize={usersPageSize}
            total={usersTotal}
            onPage={setUsersPage}
            onPageSize={setUsersPageSize}
            testIdPrefix="admin-users"
          />

          {volunteerDialog && (
            <VolunteerRoleDialog
              target={volunteerDialog}
              onClose={() => setVolunteerDialog(null)}
              onSaved={() => loadUsers()}
            />
          )}

          {addAthleteOpen && (
            <div
              className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
              onClick={() => !addBusy && setAddAthleteOpen(false)}
              data-testid="admin-add-athlete-modal"
            >
              <GlassPanel
                className="w-full max-w-2xl mx-4 p-6 max-h-[92vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="gps-label text-cyan-400">ONBOARD NEW ATHLETE</div>
                  <button
                    onClick={() => setAddAthleteOpen(false)}
                    className="text-mute hover:text-white"
                    disabled={addBusy}
                    data-testid="admin-add-athlete-close"
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="gps-label">NAME *</div>
                    <input
                      className="gps-input w-full"
                      value={newAthlete.name}
                      onChange={(e) => setNewAthlete((s) => ({ ...s, name: e.target.value }))}
                      autoFocus
                      data-testid="admin-add-athlete-name"
                    />
                  </div>
                  <div>
                    <div className="gps-label">EMAIL (login username) *</div>
                    <input
                      type="email"
                      className="gps-input w-full"
                      value={newAthlete.email}
                      onChange={(e) => setNewAthlete((s) => ({ ...s, email: e.target.value }))}
                      data-testid="admin-add-athlete-email"
                    />
                  </div>
                  <div>
                    <div className="gps-label">PHONE (used as default password) *</div>
                    <input
                      type="tel"
                      className="gps-input w-full"
                      pattern="[+0-9\s\-()]{6,20}"
                      value={newAthlete.phone}
                      onChange={(e) => setNewAthlete((s) => ({ ...s, phone: e.target.value }))}
                      data-testid="admin-add-athlete-phone"
                    />
                    <div className="text-mute text-[10px] gps-mono mt-1 tracking-[0.15em]">
                      THE ATHLETE'S INITIAL PASSWORD IS THEIR PHONE, NORMALIZED TO
                      DIGITS + LEADING '+'. E.G. "+1 (555) 000-1111" → PASSWORD "+15550001111".
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="gps-label">GENDER *</div>
                      <select
                        className="gps-input w-full"
                        value={newAthlete.gender}
                        onChange={(e) => setNewAthlete((s) => ({ ...s, gender: e.target.value }))}
                        data-testid="admin-add-athlete-gender"
                      >
                        <option value="M">M</option>
                        <option value="F">F</option>
                      </select>
                    </div>
                    <div>
                      <div className="gps-label">DATE OF BIRTH *</div>
                      <input
                        type="date"
                        className="gps-input w-full"
                        max={new Date(Date.now() - 86400000).toISOString().slice(0, 10)}
                        value={newAthlete.dob}
                        onChange={(e) => setNewAthlete((s) => ({ ...s, dob: e.target.value }))}
                        data-testid="admin-add-athlete-dob"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="gps-label">COUNTRY *</div>
                      <input
                        className="gps-input w-full"
                        value={newAthlete.country}
                        onChange={(e) => setNewAthlete((s) => ({ ...s, country: e.target.value }))}
                        data-testid="admin-add-athlete-country"
                      />
                    </div>
                    <div>
                      <div className="gps-label">CITY *</div>
                      <input
                        className="gps-input w-full"
                        value={newAthlete.city}
                        onChange={(e) => setNewAthlete((s) => ({ ...s, city: e.target.value }))}
                        data-testid="admin-add-athlete-city"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <div className="gps-label">ATHLETE CLASS *</div>
                      <select
                        className="gps-input w-full"
                        value={newAthlete.athlete_class}
                        onChange={(e) => setNewAthlete((s) => ({ ...s, athlete_class: e.target.value }))}
                        data-testid="admin-add-athlete-class"
                      >
                        {ATHLETE_CLASSES.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="gps-label">PRIMARY SPORT *</div>
                      <select
                        className="gps-input w-full"
                        value={newAthlete.primary_sport}
                        onChange={(e) => setNewAthlete((s) => ({ ...s, primary_sport: e.target.value }))}
                        data-testid="admin-add-athlete-primary-sport"
                      >
                        <option value="">— select —</option>
                        {sports.map((sp) => (
                          <option key={sp} value={sp}>{sp}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div className="gps-label">INSTITUTION / GYM *</div>
                    <input
                      className="gps-input w-full"
                      list="admin-add-athlete-gym-options"
                      value={newAthlete.gym}
                      onChange={(e) => setNewAthlete((s) => ({ ...s, gym: e.target.value }))}
                      data-testid="admin-add-athlete-gym"
                    />
                    <datalist id="admin-add-athlete-gym-options">
                      {(gyms || []).map((g) => (
                        <option key={g.name || g} value={g.name || g} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <div className="mt-6 flex gap-2 justify-end">
                  <button
                    onClick={() => setAddAthleteOpen(false)}
                    className="gps-btn ghost"
                    disabled={addBusy}
                    data-testid="admin-add-athlete-cancel"
                  >
                    CANCEL
                  </button>
                  <button
                    onClick={createAthlete}
                    className="gps-btn"
                    disabled={addBusy}
                    data-testid="admin-add-athlete-submit"
                  >
                    {addBusy ? "ONBOARDING..." : "ONBOARD"}
                  </button>
                </div>
              </GlassPanel>
            </div>
          )}
        </>
      )}

      {tab === "scores" && (
        <>
          <GlassPanel className="mt-6 p-4 flex flex-wrap gap-3 items-center" data-testid="admin-scores-bar">
            <div>
              <div className="gps-label">SCORE VERIFICATION</div>
              <div className="text-mute text-xs">Verified scores power the Pro leaderboard and Records page. Unverified scores still show on the Open leaderboard.</div>
            </div>
            <div className="flex-1" />
            <div className="relative w-full sm:w-72">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute pointer-events-none" />
              <input
                className="gps-input w-full pl-8 pr-8"
                placeholder="SEARCH BY NAME, EMAIL, ID…"
                value={scoresQuery}
                onChange={(e) => setScoresQuery(e.target.value)}
                data-testid="admin-scores-search"
              />
              {scoresQuery && (
                <button
                  onClick={() => setScoresQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-mute hover:text-white"
                  aria-label="Clear score search"
                  data-testid="admin-scores-search-clear"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            <button
              onClick={() => setScoreDialog({ mode: "create" })}
              className="gps-btn"
              data-testid="admin-scores-add"
              title="Add a new score for any athlete"
            >
              <Plus size={12} /> ADD SCORE
            </button>
            <div className="flex gap-2 items-center">
              <span className="gps-mono text-mute text-[10px] tracking-[0.2em]">EXPORT</span>
              <button onClick={() => exportScoresCsv("all")} className="gps-btn ghost" data-testid="admin-scores-export-all">
                <Download size={12} /> ALL
              </button>
              <button onClick={() => exportScoresCsv("pending")} className="gps-btn ghost" data-testid="admin-scores-export-pending">
                <Download size={12} /> PENDING
              </button>
              <button onClick={() => exportScoresCsv("verified")} className="gps-btn ghost" data-testid="admin-scores-export-verified">
                <Download size={12} /> VERIFIED
              </button>
            </div>
            <select className="gps-input w-44" value={scoresFilter} onChange={(e) => setScoresFilter(e.target.value)} data-testid="admin-scores-filter">
              <option value="pending">PENDING REVIEW</option>
              <option value="verified">VERIFIED</option>
              <option value="all">ALL</option>
            </select>
          </GlassPanel>

          <GlassPanel
            className="mt-4 overflow-x-auto"
            data-testid="admin-scores-table"
            style={{ transform: "translateZ(0)", willChange: "scroll-position" }}
          >
            <table className="w-full text-sm border-collapse min-w-[1400px]">
              <thead>
                <tr className="border-b border-white/15">
                  {SCORES_COLS.map((c) => (
                    <SortableTh
                      key={c.key || c.label}
                      col={c}
                      sort={scoresSort}
                      onToggle={toggleScoresSort}
                      testIdPrefix="admin-scores-sort"
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedScores.map((s) => (
                  <tr
                    key={s.id}
                    className="border-b border-white/5"
                    data-testid={`admin-score-row-${s.id}`}
                    style={{ contain: "layout paint style", contentVisibility: "auto", containIntrinsicSize: "0 56px" }}
                  >
                    <td className="p-3 gps-mono text-xs text-mute">{new Date(s.created_at).toLocaleString()}</td>
                    <td className="p-3 text-white">{s.snapshot?.name || s.owner?.name || "—"}</td>
                    <td className="p-3 gps-mono text-xs text-mute">{s.owner?.email}</td>
                    <td className="p-3 gps-mono text-xs text-white">{s.snapshot?.gender || s.owner?.gender}</td>
                    <td className="p-3 gps-mono text-xs text-white text-center">{s.snapshot?.age ?? "—"}</td>
                    <td className="p-3 gps-mono text-xs">
                      <span className={s.snapshot?.athlete_class === "pro" ? "text-cyan-400" : "text-mute"}>
                        {(s.snapshot?.athlete_class || "regular").toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 gps-mono text-xs text-white whitespace-nowrap">{s.snapshot?.primary_sport || "—"}</td>
                    <td className="p-3 gps-mono text-xs text-white text-center whitespace-nowrap" data-testid={`admin-score-height-${s.id}`}>
                      {(s.height_cm ?? s.snapshot?.height_cm) ?? "—"}
                    </td>
                    <td className="p-3 gps-mono text-xs text-white text-center whitespace-nowrap" data-testid={`admin-score-weight-${s.id}`}>
                      {(s.weight_kg ?? s.snapshot?.weight_kg) ?? "—"}
                    </td>
                    <td className="p-3 big-numeric text-white">{s.gps?.toFixed?.(1)}</td>
                    <td className="p-3 gps-mono text-xs">
                      <span className="text-white">{s.archetype?.code}</span>
                      <span className="text-mute ml-2">{s.archetype?.name}</span>
                    </td>
                    <td className="p-3">
                      {s.verified ? (
                        <span className="gps-tag" style={{ borderColor: "rgba(0,240,255,0.6)", color: "#00f0ff" }}>✓ VERIFIED</span>
                      ) : (
                        <span className="gps-tag">PENDING</span>
                      )}
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => openScoreCard(s)}
                        className="gps-btn ghost"
                        data-testid={`admin-score-card-${s.id}`}
                        title="Open & download GPS Card for this score"
                      >
                        <Download size={12} /> CARD
                      </button>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1">
                        {s.verified ? (
                          <button onClick={() => verifyScore(s, false)} className="gps-btn ghost" data-testid={`admin-score-unverify-${s.id}`}>
                            <XCircle size={12} /> UNVERIFY
                          </button>
                        ) : (
                          <button onClick={() => verifyScore(s, true)} className="gps-btn" data-testid={`admin-score-verify-${s.id}`}>
                            <ShieldCheck size={12} /> VERIFY
                          </button>
                        )}
                        <button
                          onClick={() => setScoreDialog({ mode: "edit", score: s })}
                          className="gps-btn ghost"
                          aria-label="Edit score"
                          title="Edit height, weight and test values (triggers GPS + archetype recompute)"
                          data-testid={`admin-score-edit-${s.id}`}
                        >
                          <Pencil size={12} />
                        </button>
                        <button
                          onClick={() => deleteScore(s)}
                          className="gps-btn ghost"
                          aria-label="Delete score"
                          title="Delete this score entry"
                          data-testid={`admin-score-delete-${s.id}`}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedScores.length === 0 && (
                  <tr><td colSpan={14} className="p-8 text-center text-mute gps-mono text-xs">NO SCORES</td></tr>
                )}
              </tbody>
            </table>
          </GlassPanel>
          <PaginationBar
            page={scoresPage}
            pageSize={scoresPageSize}
            total={scoresTotal}
            onPage={setScoresPage}
            onPageSize={setScoresPageSize}
            testIdPrefix="admin-scores"
          />
          {scoreDialog && (
            <AdminScoreDialog
              mode={scoreDialog.mode}
              initialScore={scoreDialog.score}
              onClose={() => setScoreDialog(null)}
              onSaved={() => loadScores()}
            />
          )}
        </>
      )}

      {tab === "gyms" && (
        <>
          <GlassPanel className="mt-6 p-4 flex flex-wrap gap-3 items-center" data-testid="admin-gym-add-bar">
            <div>
              <div className="gps-label">ADD INSTITUTION</div>
              <div className="text-mute text-xs">Adds an admin-approved institution, visible in leaderboard filter and profile picker.</div>
            </div>
            <form onSubmit={addGym} className="flex-1 flex gap-2 min-w-[260px]">
              <input
                className="gps-input flex-1"
                placeholder="e.g. Tokyo Combat Lab"
                value={newGymName}
                onChange={(ev) => setNewGymName(ev.target.value)}
                data-testid="admin-gym-new-name"
              />
              <button type="submit" className="gps-btn" data-testid="admin-gym-add-btn">
                <Save size={14} /> ADD
              </button>
            </form>
          </GlassPanel>

          <GlassPanel className="mt-4 overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-white/15">
                {GYMS_COLS.map((c) => (
                  <SortableTh
                    key={c.key || c.label}
                    col={c}
                    sort={gymsSort}
                    onToggle={toggleGymsSort}
                    testIdPrefix="admin-gyms-sort"
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedGyms.map((g) => (
                <tr key={g.name} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-3">
                    {gymEdits[g.name] !== undefined ? (
                      <div className="flex items-center gap-2">
                        <input
                          className="gps-input"
                          value={gymEdits[g.name]}
                          onChange={(ev) => setGymEdits((s) => ({ ...s, [g.name]: ev.target.value }))}
                          data-testid={`admin-gym-rename-input-${g.name}`}
                          autoFocus
                        />
                      </div>
                    ) : (
                      <span className="text-white">{g.name}</span>
                    )}
                  </td>
                  <td className="p-3 big-numeric text-white">{g.athletes}</td>
                  <td className="p-3">
                    {g.pending_approval ? (
                      <span className="gps-tag" style={{ borderColor: "rgba(255,200,0,0.7)", color: "#ffd166" }} data-testid={`admin-gym-pending-${g.name}`}>
                        PENDING APPROVAL
                      </span>
                    ) : g.listed ? (
                      <span className="gps-tag">LISTED</span>
                    ) : (
                      <span className="gps-tag" style={{ borderColor: "rgba(255,80,80,0.5)", color: "#ff8a8a" }}>DELISTED</span>
                    )}
                    {g.submitted_by && g.pending_approval && (
                      <div className="text-mute text-[10px] gps-mono mt-1 tracking-[0.15em]">
                        FROM · {g.submitted_by}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex gap-1 flex-wrap">
                      {gymEdits[g.name] !== undefined ? (
                        <>
                          <button
                            onClick={() => saveGymRename(g)}
                            className="gps-btn"
                            data-testid={`admin-gym-rename-save-${g.name}`}
                          >
                            <Save size={12} /> SAVE
                          </button>
                          <button
                            onClick={() => setGymEdits((s) => { const c = { ...s }; delete c[g.name]; return c; })}
                            className="gps-btn ghost"
                            data-testid={`admin-gym-rename-cancel-${g.name}`}
                          >
                            CANCEL
                          </button>
                        </>
                      ) : (
                        <>
                          {g.pending_approval && (
                            <button
                              onClick={() => approveGym(g)}
                              className="gps-btn"
                              data-testid={`admin-gym-approve-${g.name}`}
                              style={{ borderColor: "rgba(0,240,255,0.7)", boxShadow: "0 0 12px rgba(0,240,255,0.35)" }}
                            >
                              <CheckCircle2 size={12} /> APPROVE
                            </button>
                          )}
                          <button
                            onClick={() => setGymEdits((s) => ({ ...s, [g.name]: g.name }))}
                            className="gps-btn ghost"
                            data-testid={`admin-gym-rename-${g.name}`}
                            aria-label="Rename institution"
                          >
                            <Pencil size={12} /> RENAME
                          </button>
                          <button onClick={() => toggleGymListed(g)} className="gps-btn ghost" data-testid={`admin-gym-toggle-${g.name}`}>
                            {g.listed ? "DELIST" : "LIST"}
                          </button>
                          <button
                            onClick={() => deleteGym(g)}
                            className="gps-btn ghost"
                            data-testid={`admin-gym-delete-${g.name}`}
                            aria-label="Delete institution"
                          >
                            <Trash2 size={12} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {sortedGyms.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-mute gps-mono text-xs">NO INSTITUTIONS</td></tr>}
            </tbody>
          </table>
        </GlassPanel>
        <PaginationBar
          page={gymsPage}
          pageSize={gymsPageSize}
          total={gymsTotal}
          onPage={setGymsPage}
          onPageSize={setGymsPageSize}
          testIdPrefix="admin-gyms"
        />
        </>
      )}
      {tab === "tests" && (
        <GlassPanel className="mt-6 overflow-x-auto" data-testid="admin-tests-panel">
          <table className="w-full text-sm min-w-[900px] border-collapse">
            <thead>
              <tr className="border-b border-white/15">
                {["Test", "Unit", "Q (M)", "D (M)", "R (M)", "Q (F)", "D (F)", "R (F)", "Status", "Action"].map((h) => (
                  <th key={h} className="gps-heading text-xs tracking-[0.22em] text-mute font-medium uppercase text-left p-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tests.map((t) => {
                const e = testEdits[t.key] || {};
                const m = e.qdr_M || t.qdr_M;
                const f = e.qdr_F || t.qdr_F;
                return (
                  <tr key={t.key} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <div className="text-white">{t.name}</div>
                      <div className="text-mute text-[10px] gps-mono tracking-[0.18em] mt-1">
                        {t.lower_better ? "LOWER BETTER" : "HIGHER BETTER"} · {t.pillar.toUpperCase()}
                      </div>
                    </td>
                    <td className="p-3 text-mute gps-mono text-xs">{t.unit}</td>
                    {[0, 1, 2].map((i) => (
                      <td key={`m-${i}`} className="p-3">
                        <input
                          type="number"
                          step="any"
                          className="gps-input w-24"
                          value={m[i]}
                          onChange={(ev) => setTestQDR(t.key, "M", i, ev.target.value)}
                          data-testid={`qdr-${t.key}-M-${i}`}
                        />
                      </td>
                    ))}
                    {[0, 1, 2].map((i) => (
                      <td key={`f-${i}`} className="p-3">
                        <input
                          type="number"
                          step="any"
                          className="gps-input w-24"
                          value={f[i]}
                          onChange={(ev) => setTestQDR(t.key, "F", i, ev.target.value)}
                          data-testid={`qdr-${t.key}-F-${i}`}
                        />
                      </td>
                    ))}
                    <td className="p-3">
                      {t.overridden
                        ? <span className="gps-tag">OVERRIDDEN</span>
                        : <span className="gps-mono text-mute text-[10px] tracking-[0.18em]">DEFAULT</span>}
                    </td>
                    <td className="p-3 flex gap-2">
                      <button onClick={() => saveTest(t)} className="gps-btn ghost" disabled={!testEdits[t.key]} data-testid={`qdr-save-${t.key}`}>
                        <Save size={12} /> SAVE
                      </button>
                      {t.overridden && (
                        <button onClick={() => resetTest(t)} className="gps-btn ghost" data-testid={`qdr-reset-${t.key}`}>
                          RESET
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {tests.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-mute gps-mono text-xs">NO TESTS</td></tr>}
            </tbody>
          </table>
          <div className="p-4 text-mute gps-mono text-[10px] tracking-[0.18em]">
            CONSTRAINTS · HIGHER-BETTER: Q &lt; D &lt; R · LOWER-BETTER (TIME): Q &gt; D &gt; R
          </div>
        </GlassPanel>
      )}

      {tab === "events" && (
        <>
          <GlassPanel className="mt-6 p-6" data-testid="admin-event-form">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
              <div>
                <div className="gps-label">{eventForm.id ? "EDIT EVENT" : "CREATE EVENT"}</div>
                <h2 className="gps-heading text-xl text-white">
                  {eventForm.id ? eventForm.name.toUpperCase() : "NEW EVENT"}
                </h2>
              </div>
              {eventForm.id && (
                <button
                  type="button"
                  onClick={() => setEventForm(emptyEvent)}
                  className="gps-btn ghost"
                  data-testid="admin-event-cancel-edit"
                >
                  <X size={14} /> CANCEL EDIT
                </button>
              )}
            </div>
            <form onSubmit={saveEvent} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="gps-label">NAME</label>
                <input
                  className="gps-input"
                  required
                  value={eventForm.name}
                  onChange={(ev) => setEventField("name", ev.target.value)}
                  data-testid="admin-event-name"
                />
              </div>
              <div>
                <label className="gps-label">DATE</label>
                <input
                  type="date"
                  className="gps-input"
                  required
                  value={eventForm.date}
                  onChange={(ev) => setEventField("date", ev.target.value)}
                  data-testid="admin-event-date"
                />
              </div>
              <div>
                <label className="gps-label">LOCATION</label>
                <input
                  className="gps-input"
                  required
                  placeholder="e.g. Bengaluru, IN"
                  value={eventForm.location}
                  onChange={(ev) => setEventField("location", ev.target.value)}
                  data-testid="admin-event-location"
                />
              </div>
              <div className="md:col-span-2">
                <label className="gps-label">SUMMARY</label>
                <textarea
                  className="gps-input"
                  rows={3}
                  value={eventForm.summary}
                  onChange={(ev) => setEventField("summary", ev.target.value)}
                  data-testid="admin-event-summary"
                />
              </div>
              <div>
                <label className="gps-label">STATUS</label>
                <select
                  className="gps-input"
                  value={eventForm.status}
                  onChange={(ev) => setEventField("status", ev.target.value)}
                  data-testid="admin-event-status"
                >
                  <option value="upcoming">UPCOMING</option>
                  <option value="live">LIVE</option>
                  <option value="past">PAST</option>
                </select>
              </div>
              <div>
                <label className="gps-label">REGISTRATION URL</label>
                <input
                  className="gps-input"
                  type="url"
                  placeholder="https://"
                  value={eventForm.registration_url}
                  onChange={(ev) => setEventField("registration_url", ev.target.value)}
                  data-testid="admin-event-reg-url"
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <button type="submit" className="gps-btn" data-testid="admin-event-save">
                  {eventForm.id ? <><Save size={14} /> UPDATE</> : <><Plus size={14} /> CREATE EVENT</>}
                </button>
              </div>
            </form>
          </GlassPanel>

          <GlassPanel className="mt-4 overflow-x-auto" data-testid="admin-events-table">
            <table className="w-full text-sm border-collapse min-w-[900px]">
              <thead>
                <tr className="border-b border-white/15">
                  {EVENTS_COLS.map((c) => (
                    <SortableTh
                      key={c.key || c.label}
                      col={c}
                      sort={eventsSort}
                      onToggle={toggleEventsSort}
                      testIdPrefix="admin-events-sort"
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedEvents.map((e) => (
                  <tr key={e.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-event-row-${e.id}`}>
                    <td className="p-3 gps-mono text-xs text-white">{e.date}</td>
                    <td className="p-3">
                      <div className="text-white">{e.name}</div>
                      {e.summary && <div className="text-mute text-[11px] mt-1 max-w-md">{e.summary}</div>}
                    </td>
                    <td className="p-3 text-mute gps-mono text-xs">{e.location}</td>
                    <td className="p-3">
                      <span className={`gps-tag ${e.status === "live" ? "text-cyan" : ""}`}>
                        {(e.status || "upcoming").toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 big-numeric text-white">{e.interested_count || 0}</td>
                    <td className="p-3">
                      <div className="flex gap-1 flex-wrap">
                        <button onClick={() => editEvent(e)} className="gps-btn ghost" data-testid={`admin-event-edit-${e.id}`}>
                          EDIT
                        </button>
                        {(e.status === "live" || e.status === "upcoming") && (
                          <button
                            onClick={() => setScoringEvent(e)}
                            className="gps-btn"
                            data-testid={`admin-event-score-${e.id}`}
                            title="Enter staged scores for this event"
                          >
                            <Trophy size={12} /> SCORE
                          </button>
                        )}
                        <button onClick={() => viewInterested(e)} className="gps-btn ghost" data-testid={`admin-event-interested-${e.id}`}>
                          <UsersIcon size={12} /> VIEW
                        </button>
                        <button onClick={() => downloadInterestCsv(e)} className="gps-btn ghost" data-testid={`admin-event-csv-${e.id}`}>
                          <Download size={12} /> CSV
                        </button>
                        <button onClick={() => deleteEvent(e)} className="gps-btn ghost" data-testid={`admin-event-delete-${e.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedEvents.length === 0 && (
                  <tr><td colSpan={6} className="p-8 text-center text-mute gps-mono text-xs">NO EVENTS</td></tr>
                )}
              </tbody>
            </table>
          </GlassPanel>
          <PaginationBar
            page={eventsPage}
            pageSize={eventsPageSize}
            total={eventsTotal}
            onPage={setEventsPage}
            onPageSize={setEventsPageSize}
            testIdPrefix="admin-events"
          />

          {interestModal && (
            <div
              className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto"
              onClick={() => setInterestModal(null)}
              data-testid="admin-event-interest-modal"
            >
              <GlassPanel
                className="p-6 w-full max-w-4xl max-h-[88vh] overflow-y-auto relative"
                scanline
                onClick={(ev) => ev.stopPropagation()}
              >
                <button
                  onClick={() => setInterestModal(null)}
                  className="absolute top-4 right-4 text-mute hover:text-white"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
                <div className="gps-label">INTERESTED ATHLETES</div>
                <h3 className="gps-heading text-2xl text-white">
                  {interestModal.event.name.toUpperCase()}
                </h3>
                <div className="text-mute gps-mono text-xs mt-1">
                  <Calendar size={11} className="inline mr-1" /> {interestModal.event.date} · {interestModal.event.location}
                </div>

                <div className="flex justify-end mt-4">
                  <button onClick={() => downloadInterestCsv(interestModal.event)} className="gps-btn" data-testid="admin-event-modal-csv">
                    <Download size={14} /> DOWNLOAD CSV
                  </button>
                </div>

                <div className="overflow-x-auto mt-4">
                  <table className="w-full text-sm border-collapse min-w-[800px]">
                    <thead>
                      <tr className="border-b border-white/15">
                        {["Date", "Name", "Email", "Phone", "Country", "Gym", "GPS", "Archetype"].map((h) => (
                          <th key={h} className="gps-heading text-xs tracking-[0.22em] text-mute font-medium uppercase text-left p-3">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {interestModal.athletes.map((a) => (
                        <tr key={a.id} className="border-b border-white/5">
                          <td className="p-3 gps-mono text-xs text-mute">
                            {a.interested_at ? new Date(a.interested_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="p-3 text-white">{a.name}</td>
                          <td className="p-3 gps-mono text-xs text-mute">{a.email}</td>
                          <td className="p-3 gps-mono text-xs text-mute">{a.phone}</td>
                          <td className="p-3 text-mute">{a.country}</td>
                          <td className="p-3 text-mute">{a.gym || "—"}</td>
                          <td className="p-3 big-numeric text-white">
                            {a.current_gps != null ? a.current_gps.toFixed(1) : "—"}
                          </td>
                          <td className="p-3 gps-mono text-xs text-white">{a.archetype_code || "—"}</td>
                        </tr>
                      ))}
                      {interestModal.athletes.length === 0 && (
                        <tr><td colSpan={8} className="p-8 text-center text-mute gps-mono text-xs">NO ATHLETES YET</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </GlassPanel>
            </div>
          )}

          {scoringEvent && (
            <EventScoringModal
              event={scoringEvent}
              onClose={() => setScoringEvent(null)}
            />
          )}
        </>
      )}

      {tab === "active" && (
        <>
          <GlassPanel className="mt-6 p-4 flex flex-wrap gap-3 items-center" data-testid="admin-active-bar">
            <div>
              <div className="gps-label">ACTIVE USERS</div>
              <div className="text-mute text-xs">Athletes who logged in within the selected window. Tracks last_login on every successful sign-in.</div>
            </div>
            <div className="flex-1" />
            <select
              className="gps-input w-44"
              value={activeWindow}
              onChange={(e) => setActiveWindow(parseInt(e.target.value, 10))}
              data-testid="admin-active-window"
            >
              <option value={1}>LAST 24 HOURS</option>
              <option value={7}>LAST 7 DAYS</option>
              <option value={30}>LAST 30 DAYS</option>
            </select>
            <button onClick={downloadActiveCsv} className="gps-btn" data-testid="admin-active-csv">
              <Download size={14} /> EXPORT CSV
            </button>
          </GlassPanel>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4" data-testid="admin-active-stats">
            {[
              { label: "DAU · 24H", value: activeData.dau },
              { label: "WAU · 7D", value: activeData.wau },
              { label: "MAU · 30D", value: activeData.mau },
            ].map((b) => (
              <GlassPanel key={b.label} className="p-5">
                <div className="gps-label">{b.label}</div>
                <div className="big-numeric text-white text-4xl mt-2">{b.value ?? 0}</div>
              </GlassPanel>
            ))}
          </div>

          <GlassPanel className="mt-4 overflow-x-auto" data-testid="admin-active-table">
            <table className="w-full text-sm border-collapse min-w-[900px]">
              <thead>
                <tr className="border-b border-white/15">
                  {ACTIVE_COLS.map((c) => (
                    <SortableTh
                      key={c.key || c.label}
                      col={c}
                      sort={activeSort}
                      onToggle={toggleActiveSort}
                      testIdPrefix="admin-active-sort"
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedActive.map((u) => (
                  <tr key={u.id} className="border-b border-white/5" data-testid={`admin-active-row-${u.id}`}>
                    <td className="p-3 gps-mono text-xs text-mute">
                      {u.last_login ? new Date(u.last_login).toLocaleString() : "—"}
                    </td>
                    <td className="p-3 text-white">{u.name}</td>
                    <td className="p-3 gps-mono text-xs text-mute">{u.email}</td>
                    <td className="p-3 gps-mono text-xs text-mute">{u.phone}</td>
                    <td className="p-3 text-mute">{u.country}</td>
                    <td className="p-3 text-mute">{u.gym || "—"}</td>
                    <td className="p-3 big-numeric text-white">
                      {u.current_gps != null ? u.current_gps.toFixed(1) : "—"}
                    </td>
                    <td className="p-3 big-numeric text-cyan">{u.login_count || 0}</td>
                  </tr>
                ))}
                {(!sortedActive || sortedActive.length === 0) && (
                  <tr>
                    <td colSpan={8} className="p-8 text-center text-mute gps-mono text-xs">
                      <Activity size={14} className="inline mr-2 -mt-0.5" />
                      NO ACTIVE USERS IN THIS WINDOW
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </GlassPanel>
          <PaginationBar
            page={activePage}
            pageSize={activePageSize}
            total={activeTotal}
            onPage={setActivePage}
            onPageSize={setActivePageSize}
            testIdPrefix="admin-active"
          />
        </>
      )}

      {tab === "resets" && (
        <>
          <GlassPanel className="mt-6 p-4 flex flex-wrap gap-3 items-center" data-testid="admin-resets-bar">
            <div>
              <div className="gps-label">PASSWORD RESETS</div>
              <div className="text-mute text-xs max-w-3xl">
                One code per request, generated automatically when the user clicks <span className="text-white">FORGOT PASSWORD</span>.
                The user&apos;s old password stops working the instant they submit the request — they need this temp code to log back in.
                <br />
                <span className="text-white">1.</span> Click the code to select it, then copy with Ctrl/Cmd+C.
                <span className="text-white"> 2.</span> Paste into WhatsApp / SMS and send to the user.
                <span className="text-white"> 3.</span> Click <span className="text-white">MARK SENT</span> to move it out of pending.
                <br />
                <span className="text-white">DISMISS</span> only for fraudulent / duplicate requests (note: the code stays active for 24h regardless).
              </div>
            </div>
            <div className="flex-1" />
            <select
              className="gps-input w-44"
              value={pwResetFilter}
              onChange={(e) => setPwResetFilter(e.target.value)}
              data-testid="admin-resets-filter"
            >
              <option value="pending">PENDING</option>
              <option value="issued">ISSUED</option>
              <option value="cancelled">DISMISSED</option>
              <option value="all">ALL</option>
            </select>
          </GlassPanel>

          <GlassPanel className="mt-4 overflow-x-auto" data-testid="admin-resets-table">
            <table className="w-full text-sm border-collapse min-w-[1000px]">
              <thead>
                <tr className="border-b border-white/15">
                  {RESETS_COLS.map((c) => (
                    <SortableTh
                      key={c.key || c.label}
                      col={c}
                      sort={resetsSort}
                      onToggle={toggleResetsSort}
                      testIdPrefix="admin-resets-sort"
                    />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedResets.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-reset-row-${r.id}`}>
                    <td className="p-3 gps-mono text-xs text-mute whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="p-3 text-white whitespace-nowrap">{r.user_name || "—"}</td>
                    <td className="p-3 gps-mono text-xs text-mute">
                      <div>{r.user_email}</div>
                      {r.user_phone && <div className="opacity-70">{r.user_phone}</div>}
                    </td>
                    <td className="p-3">
                      {r.status === "pending" && r.temp_password ? (
                        <code
                          className="font-mono text-base text-white bg-black/60 border border-cyan-400/40 px-3 py-1.5 rounded select-all cursor-text"
                          data-testid={`admin-reset-code-${r.id}`}
                          title="Tap to select, then copy"
                        >
                          {r.temp_password}
                        </code>
                      ) : (
                        <span className="gps-mono text-mute text-[10px] tracking-[0.2em]">
                          {r.status === "issued" ? "SHARED · CODE HIDDEN" : "—"}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      {r.status === "pending" && <span className="gps-tag">PENDING</span>}
                      {r.status === "issued" && <span className="gps-tag" style={{ borderColor: "rgba(0,240,255,0.6)", color: "#00f0ff" }}>ISSUED{r.expires_at ? ` · ${new Date(r.expires_at).toLocaleDateString()}` : ""}</span>}
                      {r.status === "cancelled" && <span className="gps-tag" style={{ borderColor: "rgba(255,80,80,0.5)", color: "#ff8a8a" }}>DISMISSED</span>}
                    </td>
                    <td className="p-3">
                      {r.status === "pending" ? (
                        <div className="flex gap-1 flex-wrap">
                          <button
                            onClick={() => markSent(r)}
                            className="gps-btn"
                            data-testid={`admin-reset-complete-${r.id}`}
                            title="Move to ISSUED after sharing the code"
                          >
                            <CheckCircle2 size={12} /> MARK SENT
                          </button>
                          <button
                            onClick={() => cancelPwReset(r)}
                            className="gps-btn ghost"
                            data-testid={`admin-reset-cancel-${r.id}`}
                            title="Discard fraudulent/duplicate request"
                          >
                            <XCircle size={12} /> DISMISS
                          </button>
                        </div>
                      ) : (
                        <span className="text-mute gps-mono text-[10px] tracking-[0.2em]">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {sortedResets.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-mute gps-mono text-xs">
                      NO {pwResetFilter === "cancelled" ? "DISMISSED" : pwResetFilter.toUpperCase()} REQUESTS
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </GlassPanel>
          <PaginationBar
            page={pwResetsPage}
            pageSize={pwResetsPageSize}
            total={pwResetsTotal}
            onPage={setPwResetsPage}
            onPageSize={setPwResetsPageSize}
            testIdPrefix="admin-resets"
          />
        </>
      )}

      {tab === "settings" && (
        <>
          <GlassPanel className="mt-6 p-6" data-testid="admin-settings-panel">
            <div className="gps-label">PLATFORM CONTACT</div>
            <div className="text-mute text-xs max-w-2xl mt-1">
              These details power the <span className="text-white">CONTACT US</span> block in the
              footer of every public page. Leave a field blank to hide that row from the footer.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 max-w-3xl">
              <div>
                <label className="gps-label flex items-center gap-2"><Mail size={12} /> CONTACT EMAIL</label>
                <input
                  type="email"
                  className="gps-input mt-1"
                  placeholder="hello@example.com"
                  value={settings.contact_email}
                  onChange={(e) => setSettings((s) => ({ ...s, contact_email: e.target.value }))}
                  data-testid="admin-settings-email"
                />
              </div>
              <div>
                <label className="gps-label flex items-center gap-2"><Phone size={12} /> CONTACT PHONE</label>
                <input
                  type="tel"
                  className="gps-input mt-1"
                  placeholder="+91 90000 00000"
                  value={settings.contact_phone}
                  onChange={(e) => setSettings((s) => ({ ...s, contact_phone: e.target.value }))}
                  data-testid="admin-settings-phone"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-2 items-center">
              <button
                onClick={saveSettings}
                disabled={settingsBusy}
                className="gps-btn"
                data-testid="admin-settings-save"
              >
                <Save size={14} /> {settingsBusy ? "SAVING..." : "SAVE SETTINGS"}
              </button>
              <span className="text-mute text-xs gps-mono tracking-[0.2em]">
                FOOTER REFRESHES ON NEXT PAGE LOAD
              </span>
            </div>
          </GlassPanel>
        </>
      )}

      {/* GPS Card preview modal — invoked from any score row */}
      {cardPayload && (
        <div
          className="fixed inset-0 z-50 grid place-items-center p-4 pt-20 bg-black/80 backdrop-blur-sm overflow-y-auto"
          onClick={() => setCardPayload(null)}
          data-testid="admin-card-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="relative">
            <button
              onClick={() => setCardPayload(null)}
              className="absolute -top-12 right-0 grid place-items-center w-9 h-9 rounded-full border border-white/30 text-white hover:bg-white/10"
              aria-label="Close card preview"
              data-testid="admin-card-close"
            >
              <X size={16} />
            </button>
            <div ref={cardRef}>
              <PlayerCard
                name={cardPayload.name}
                avatarUrl={cardPayload.avatarUrl}
                date={cardPayload.date}
                gps={cardPayload.gps}
                archetype={cardPayload.archetype}
                pillars={cardPayload.pillars}
                country={cardPayload.country}
                gym={cardPayload.gym}
                verified={cardPayload.verified}
                publicId={cardPayload.publicId}
              />
            </div>
            <div className="mt-4 flex justify-center">
              <button
                onClick={downloadCard}
                disabled={cardBusy}
                className="gps-btn"
                data-testid="admin-card-download"
              >
                <Download size={14} /> {cardBusy ? "RENDERING..." : "DOWNLOAD PNG"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
